/**
 * Landing Page — ProcureX
 * Inspired by capexara.com/platforms/samgraha: the source-to-settle spine
 * for capital projects. Brand colors lifted from the new logo —
 *   navy:    #1E4E8C  (primary)
 *   navy-2:  #0A1E3A  (depth)
 *   accent:  #F26B1F  (orange — the live signal)
 *   accent-2:#2E70AD  (mid-tone)
 */
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight, Brain, CheckCircle, Database, FileText, Gavel, GitBranch,
  Layers, Shield, ShieldCheck, Sparkles, TrendingDown, Workflow,
  Building2, Mail, Phone, ChevronDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';

// -----------------------------------------------------------------------------
// Content (all copy lifted from / inspired by the Capexara ProcureX brief)
// -----------------------------------------------------------------------------
const PROBLEM_STATS = [
  { value: '60%+', label: 'of enterprise spend flows through procurement & contracts every year' },
  { value: '8–12%', label: 'average value leakage across price, scope change, and spec drift' },
  { value: '6–18 mo',label: 'typical cycle from category strategy to a signed supplier contract' },
  { value: '72%',   label: 'of disputes originate in contract ambiguity or missed clauses' },
];

const PILLARS = [
  {
    n: '01', title: 'Category Management', count: 2, accent: 'navy',
    modules: [
      { name: 'Category Taxonomy & Strategy', desc: 'UNSPSC + custom taxonomy for capital project spend. Kraljic auto-classification. Spend cube with drill-down. Should-cost modelling. Demand forecasting linked to project schedules.' },
      { name: 'Market Intelligence & Benchmarking', desc: 'Commodity price tracking (steel, copper, cement, diesel). Benchmarking from production deployments. Risk indices per category. Quarterly category review automation.' },
    ],
  },
  {
    n: '02', title: 'Supplier Management', count: 3, accent: 'navy',
    modules: [
      { name: 'Supplier Qualification & 360°', desc: 'Onboarding workflows, document expiry, financial health, performance scoring. 360° supplier view with PO/GRN/invoice/claim history in one pane.' },
      { name: 'Risk & Sanctions Screening', desc: 'OFAC / UN / SEBI / MCA screening. Concentration risk, dependency mapping, ESG diligence.' },
      { name: 'Supplier Portal (SP-10)', desc: 'Self-service for suppliers — bids, POs, ASN/GRN, invoices, claims, eAuctions — with full data isolation and role-based access.' },
    ],
  },
  {
    n: '03', title: 'Sourcing Execution', count: 2, accent: 'orange',
    modules: [
      { name: 'Sourcing Cockpit', desc: 'Nine event types — RFI, RFP, RFQ, Forward Auction, Reverse eAuction, Sealed Bid, Two-Envelope, BAFO, Framework Award — orchestrated on one canvas with real-time bid analytics and AI scoring.' },
      { name: 'Award & Handoff', desc: 'AI-drafted award notes with comparison tables, risk analysis, and policy compliance. Auto-handoff to contract drafting with full audit trail.' },
    ],
  },
  {
    n: '04', title: 'Contract Lifecycle', count: 3, accent: 'navy',
    modules: [
      { name: 'Contract Authoring & Clause Library', desc: '50,000+ annotated clauses from FIDIC, NEC, and bespoke Indian capital-project contracts. Clause-level risk scoring. Missing-clause and conflict detection.' },
      { name: 'Obligation & Variation Management', desc: 'Reads contracts and extracts every commitment and deadline. End-to-end VO workflow with impact analysis and approval routing.' },
      { name: 'e-Signature & Repository', desc: 'Single source of truth — versioned, searchable, role-secured. All party signatures captured with non-repudiation.' },
    ],
  },
  {
    n: '05', title: 'Execution & Closeout', count: 2, accent: 'orange',
    modules: [
      { name: 'Claims, LD & Disputes', desc: 'AI-drafted claim letters with clause citations and quantum models. Counter-claim analysis with merits assessment. Auto-accruing LD ledger with notice automation.' },
      { name: 'P2P, Invoicing & Closeout', desc: 'Three-way match, retention release, warranty bond tracking, final account settlement. Linked end-to-end so a paid invoice traces back to its category strategy.' },
    ],
  },
];

const SOURCING_FEATURES = [
  { n: '01', title: 'Nine event types, one interface',
    desc: 'RFI, RFP, RFQ, Forward Auction, Reverse eAuction, Sealed Bid, Two-Envelope, BAFO, Framework Award. Same cockpit, different rulesets.' },
  { n: '02', title: 'Real-time bid visibility',
    desc: 'See bids as they land. Supplier cursors, activity streams, Q&A threads, all live. Full audit trail.' },
  { n: '03', title: 'AI-powered bid analytics',
    desc: 'Anomaly detection, benchmark comparison, should-cost analysis, BATNA suggestions. A reasoning engine for negotiations.' },
  { n: '04', title: 'Technical + commercial scoring',
    desc: 'Configurable weighted scoring, multi-evaluator workflows, bias checks, audit-ready justifications.' },
  { n: '05', title: 'Auto-generated award recommendation',
    desc: 'AI drafts award-committee-ready notes with comparison tables, risk analysis, and procurement policy compliance.' },
];

const CONTRACT_AI = [
  'Clause-level risk detection — every clause scored on liability, delay, payment, termination',
  'Missing clause detection — benchmarks draft against category norms',
  'Clause conflict detection — identifies contradictions within the same contract',
  'Obligation extraction — reads contracts, extracts every commitment and deadline',
  'Claim drafting — clause citations, precedent references, quantum models',
  'Counter-claim analysis — merits assessment, weakness identification, response drafting',
];

const INTEGRATIONS = [
  { title: 'ERP Systems',          items: ['SAP S/4HANA · Oracle ERP', 'Microsoft Dynamics 365', 'Infor · Tally · Sage', 'Custom ERP via REST/SOAP'] },
  { title: 'Procurement Platforms', items: ['Coupa · Ariba (migration + coexist)', 'Jaggaer · GEP SMART', 'Ivalua · ProcurementExpress', 'Custom spend platforms'] },
  { title: 'Banks & Financial',    items: ['SWIFT messaging (MT/MX)', 'Bank APIs (SBI, ICICI, HDFC, Citi)', 'LC management · Trade finance', 'GST Suvidha Provider (GSP)'] },
  { title: 'Statutory & Compliance',items: ['GSTN for e-invoicing + returns', 'MCA21 for company data', 'BOCW / ESIC / PF for labour', 'ICEGATE · DGFT for trade'] },
];

const INDUSTRIES = [
  'Oil, Gas & Energy', 'Power & Utilities', 'Metals, Mining & Minerals',
  'Chemicals & Pharma', 'Manufacturing', 'Infrastructure', 'Marine & Shipping',
  'IT & Technology', 'BFSI', 'Retail & Consumer', 'Healthcare', 'Public Sector',
];

const USE_CASES = [
  { title: 'Direct & Indirect Procurement',
    desc: 'Day-to-day spend across raw materials, MRO, IT, services, marketing. Catalogue-driven POs, PR-to-PO automation, three-way match.' },
  { title: 'Strategic Sourcing Events',
    desc: 'RFI, RFP, RFQ, BAFO, reverse and forward auctions for any spend category. Real-time bid analytics, AI scoring, award automation.' },
  { title: 'Vendor & Supplier Management',
    desc: 'Onboarding, 360° profiles, performance scoring, document expiry tracking, risk and sanctions screening — for every supplier you work with.' },
  { title: 'Contract Lifecycle Management',
    desc: 'Author, redline, negotiate, sign, and govern contracts of any type — service agreements, MSAs, NDAs, LSTK, frameworks, SLAs.' },
  { title: 'Greenfield & Brownfield Capital Projects',
    desc: 'Full source-to-settle for $60M–$6B+ projects. LSTK contracts, FIDIC/NEC clauses, multi-year warranty, claims & disputes.' },
  { title: "Owner's Audit & Compliance",
    desc: 'Lightweight deployment for finance, audit, and compliance teams — PO approval workflows, spend dashboards, statutory reporting.' },
];

const IMPACT = [
  { value: '4.8%',  label: 'average savings on procurement spend via better sourcing',
    detail: 'A 1% improvement on a $100M buy = $1M back to the bottom line.' },
  { value: '65%',   label: 'reduction in sourcing event cycle time',
    detail: 'From 8–12 weeks to 3–4 weeks per event.' },
  { value: '72%',   label: 'reduction in claims & dispute cycle time',
    detail: 'From 180+ days to 40–60 days per claim.' },
];

const FAQS = [
  { q: 'How is ProcureX different from enterprise procurement suites?',
    a: 'Enterprise procurement suites were built for indirect spend (office supplies, software, travel). ProcureX is built native for capital projects — LSTK contracts, FIDIC/NEC clauses, multi-year warranty periods, complex claims, capex-specific categories. Most enterprises run ProcureX for capital projects and keep procurement suites for operational/indirect spend.' },
  { q: 'Can ProcureX replace our existing ERP procurement modules?',
    a: 'It can, but usually shouldn\'t. ProcureX is designed to coexist — pulling category masters from your ERP, pushing approved POs back, and owning the contract/claim layer your ERP was never built for. Replacements are scoped on a per-customer basis after a 4-week discovery.' },
  { q: 'How does the FIDIC clause library work?',
    a: 'The library ships with the full FIDIC Red, Yellow, Silver, and Pink Book families plus the NEC4 suite. Every clause is annotated with risk metadata, precedent links, and configurable variants. Drafters get clause-level suggestions; reviewers see deviation heat-maps against your house position.' },
  { q: 'Does ProcureX support Indian statutory compliance?',
    a: 'Yes — GST e-invoicing (GSP integrated), TDS deduction at source, BOCW for labour, ICEGATE for imports, MCA21 for entity validation, and SEBI screening. Output is audit-ready for CAG, ICAI, and statutory auditors.' },
  { q: 'How does the AI drafting of claims work?',
    a: 'Trained on a proprietary corpus of arbitration awards, claim correspondence, and FIDIC commentaries. It reads your contract, the timeline of events, and supporting evidence, then drafts the notice / quantum / detailed particulars with clause citations and precedent references. A senior counsel reviews — the AI does the heavy lifting.' },
  { q: 'What about data residency and security?',
    a: 'Default deployment is in-region (Mumbai for India, Frankfurt for EU). On-premise and sovereign-cloud options available. SOC-2 Type II, ISO 27001, ISO 27701 certified. Encryption at rest (AES-256) and in transit (TLS 1.3).' },
  { q: "What's the typical deployment timeline?",
    a: 'Owner-mode (audit + oversight): 4–6 weeks. Single-pillar pilot (e.g., Sourcing or Contracts): 8–12 weeks. Full source-to-settle: 16–20 weeks. Migration from legacy procurement platforms typically adds 4 weeks.' },
];

// -----------------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------------
export default function LandingPage() {
  const navigate = useNavigate();
  const [activePillar, setActivePillar] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="min-h-screen bg-white text-slate-900 overflow-hidden font-body">
      {/* Custom selection + cursor styling */}
      <style>{`
        ::selection { background: #F26B1F; color: white; }
        .grain { background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3CfeColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.04 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E"); }
      `}</style>

      {/* ─────────────────────────── NAV ─────────────────────────── */}
      <nav className="fixed top-0 inset-x-0 z-50 bg-white/85 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-16 px-6">
          <img src="/procurex-logo-light.svg" alt="ProcureX" className="h-10" data-testid="nav-logo" />
          <div className="hidden md:flex items-center gap-8 text-sm text-slate-600">
            <a href="#problem"      className="hover:text-slate-900 transition-colors">The problem</a>
            <a href="#architecture" className="hover:text-slate-900 transition-colors">Architecture</a>
            <a href="#sourcing"     className="hover:text-slate-900 transition-colors">Sourcing</a>
            <a href="#contracts"    className="hover:text-slate-900 transition-colors">Contracts</a>
            <a href="#impact"       className="hover:text-slate-900 transition-colors">Impact</a>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/sp10/login')}
                     className="text-sm text-[#F26B1F] hover:text-[#D85A14] hover:bg-[#F26B1F]/5 font-medium"
                     data-testid="nav-supplier-portal-btn">
              Supplier portal
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/login')}
                     className="text-sm text-slate-700" data-testid="nav-login-btn">
              Sign In
            </Button>
            <Button size="sm" onClick={() => navigate('/trial-signup')}
                     className="bg-[#1E4E8C] hover:bg-[#163D70] text-white text-sm"
                     data-testid="nav-trial-btn">
              Start free trial <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
        </div>
      </nav>

      {/* ─────────────────────────── HERO ─────────────────────────── */}
      <section className="relative pt-32 pb-16 px-6 grain">
        <div className="absolute top-20 right-1/4 w-[560px] h-[560px] bg-[#1E4E8C]/8 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-[#F26B1F]/10 rounded-full blur-[120px] pointer-events-none" />

        <div className="max-w-6xl mx-auto relative">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                      className="flex items-center gap-2 text-xs text-slate-500 mb-5 font-mono uppercase tracking-wider">
            <span>Platforms</span>
            <span className="text-slate-300">/</span>
            <span>Enterprise Digital</span>
            <span className="text-slate-300">/</span>
            <span className="text-[#F26B1F] font-semibold">PROCUREX</span>
          </motion.div>

          <div className="max-w-3xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <p className="text-xs text-slate-500 mb-3 font-medium">Procurement · Supply Chain · Contracts Lifecycle</p>
                <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-bold tracking-tight leading-[1.02]"
                    data-testid="hero-title">
                  The AI-native spine for{' '}
                  <span className="italic font-light text-[#1E4E8C]">procurement &amp; contracts.</span>
                </h1>
                <p className="text-lg text-slate-600 mt-7 leading-relaxed max-w-2xl">
                  From your first RFI to your final warranty release — ProcureX unifies sourcing,
                  supplier management, and contract lifecycle on one cognitive canvas. Built for
                  every procurement team, battle-tested on the world's most complex capital projects.
                </p>
                <p className="text-xs text-slate-500 mt-9 leading-relaxed max-w-xl">
                  FIDIC &amp; NEC clause libraries · ERP &amp; procurement-platform migration-ready ·
                  GST + BOCW + SEBI compliant · cloud or on-premise
                </p>
              </motion.div>
            </div>
        </div>
      </section>

      {/* ─────────────────────────── PROBLEM ─────────────────────────── */}
      <section id="problem" className="py-24 px-6 bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs font-mono uppercase tracking-widest text-[#F26B1F] mb-4">The procurement problem</p>
          <h2 className="text-3xl md:text-5xl font-heading font-bold tracking-tight leading-tight max-w-4xl">
            Your ERP handles transactions. Your SRM handles suppliers. Your CLM handles contracts.
            <span className="block italic font-light text-slate-500 mt-2">Nothing handles the whole journey.</span>
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {PROBLEM_STATS.map((s, i) => (
              <motion.div key={i}
                          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                          className="border-l-2 border-[#1E4E8C] pl-5">
                <p className="font-data text-4xl md:text-5xl font-bold text-[#1E4E8C]">{s.value}</p>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.label}</p>
              </motion.div>
            ))}
          </div>

          <p className="text-xl md:text-2xl text-slate-700 mt-16 max-w-3xl font-heading leading-snug">
            A purchase order is the first contract.
            <span className="italic font-light text-[#F26B1F]"> A contract is the last purchase order.</span>
          </p>
        </div>
      </section>

      {/* ─────────────────────────── ARCHITECTURE ─────────────────────────── */}
      <section id="architecture" className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs font-mono uppercase tracking-widest text-[#F26B1F] mb-4">Platform architecture</p>
          <h2 className="text-3xl md:text-5xl font-heading font-bold tracking-tight leading-tight max-w-3xl">
            Twelve modules.
            <span className="italic font-light text-slate-500"> One source-to-settle spine.</span>
          </h2>
          <p className="text-base text-slate-600 mt-5 max-w-2xl">
            ProcureX organises into five logical pillars — from category intelligence through
            supplier management, sourcing execution, contract lifecycle, and execution close-out.
            Click any pillar to explore.
          </p>

          {/* Pillar tabs */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 mt-12">
            {PILLARS.map((p, i) => (
              <button key={p.n}
                      onClick={() => setActivePillar(i)}
                      data-testid={`pillar-tab-${i}`}
                      className={`text-left p-5 rounded-lg border transition-all ${
                        activePillar === i
                          ? p.accent === 'orange'
                            ? 'border-[#F26B1F] bg-[#F26B1F]/5 shadow-md'
                            : 'border-[#1E4E8C] bg-[#1E4E8C]/5 shadow-md'
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-[10px] font-mono uppercase tracking-widest ${
                    activePillar === i ? (p.accent === 'orange' ? 'text-[#F26B1F]' : 'text-[#1E4E8C]') : 'text-slate-400'
                  }`}>Pillar · {p.n}</span>
                  <span className="text-[10px] text-slate-400 font-mono">{p.count}</span>
                </div>
                <div className={`text-sm font-semibold ${activePillar === i ? 'text-slate-900' : 'text-slate-700'}`}>
                  {p.title}
                </div>
              </button>
            ))}
          </div>

          {/* Active pillar modules */}
          <motion.div key={activePillar} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                      className="grid md:grid-cols-2 gap-5 mt-8">
            {PILLARS[activePillar].modules.map((m, i) => (
              <div key={i} className="p-6 rounded-xl border border-slate-200 bg-white hover:shadow-lg hover:border-[#1E4E8C]/30 transition-all"
                   data-testid={`pillar-${activePillar}-module-${i}`}>
                <h3 className="text-base font-semibold text-slate-900 mb-2">{m.name}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </motion.div>

          <p className="text-sm text-slate-500 mt-10 italic max-w-2xl">
            From the first RFI to the final warranty release — twelve modules, one cognitive spine,
            zero spreadsheets.
          </p>
        </div>
      </section>

      {/* ─────────────────────────── SOURCING ─────────────────────────── */}
      <section id="sourcing" className="py-24 px-6 bg-[#0A1E3A] text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#F26B1F]/10 rounded-full blur-[120px]" />
        <div className="max-w-6xl mx-auto relative">
          <p className="text-xs font-mono uppercase tracking-widest text-[#F26B1F] mb-4">The sourcing command canvas</p>
          <h2 className="text-3xl md:text-5xl font-heading font-bold tracking-tight leading-tight max-w-4xl">
            Every sourcing event,
            <span className="italic font-light text-slate-400"> orchestrated on one canvas.</span>
          </h2>

          {/* Live RFQ mock */}
          <div className="mt-12 rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#F26B1F]">
                  ProcureX · RFQ-2847 · Structural Steel Frame
                </span>
                <p className="text-sm text-white/60 mt-1">Reverse eAuction · Last refresh: 8s ago</p>
              </div>
              <span className="flex items-center gap-1.5 text-[10px] text-emerald-400 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE · 2d 14h
              </span>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              {/* Bid rankings */}
              <div className="rounded-lg bg-white/5 border border-white/10 p-4">
                <p className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-3">Bid Rankings</p>
                <div className="space-y-1.5">
                  {[
                    ['#1 Alpha',   '$49.7 M', '-2.3%', 'text-emerald-400'],
                    ['#2 Beta',    '$50.4 M', '-0.8%', 'text-emerald-400/70'],
                    ['#3 Gamma',   '$51.1 M', '+0.4%', 'text-white/50'],
                    ['#4 Delta',   '$51.9 M', '+2.0%', 'text-amber-400'],
                    ['#5 Epsilon', '$52.8 M', '+3.8%', 'text-amber-400'],
                    ['#6 Zeta',    '$53.6 M', '+5.4%', 'text-rose-400'],
                  ].map(([n, v, d, c]) => (
                    <div key={n} className="flex items-center justify-between text-xs">
                      <span className="text-white/70 font-mono">{n}</span>
                      <span className="font-data text-white">{v}</span>
                      <span className={`font-data ${c}`}>{d}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Timeline */}
              <div className="rounded-lg bg-white/5 border border-white/10 p-4">
                <p className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-3">Event Timeline</p>
                <div className="space-y-2">
                  {['Created', 'Invited (8)', 'Q&A Closed', 'Bidding ▶', 'Tech Eval', 'Comm Eval', 'Award'].map((s, i) => (
                    <div key={s} className="flex items-center gap-2 text-xs">
                      <span className={`w-1.5 h-1.5 rounded-full ${i < 4 ? 'bg-emerald-400' : i === 4 ? 'bg-[#F26B1F] animate-pulse' : 'bg-white/20'}`} />
                      <span className={i <= 4 ? 'text-white' : 'text-white/40'}>{s}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* AI insight */}
              <div className="rounded-lg bg-[#F26B1F]/10 border border-[#F26B1F]/30 p-4">
                <p className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#F26B1F] mb-3">
                  <Sparkles className="w-3 h-3" /> AI Insight
                </p>
                <p className="text-xs text-white/80 leading-relaxed mb-3">
                  Alpha bid 2.3% below market. Delivery risk: ±3wk variance. Recommendation: buffer schedule, request bank guarantee.
                </p>
                <p className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-2">Activity</p>
                <ul className="text-[11px] text-white/60 space-y-1">
                  <li>· Beta revised bid (2m ago)</li>
                  <li>· Gamma viewed docs (12m)</li>
                  <li>· AI flagged Delta spec (1h)</li>
                </ul>
              </div>
            </div>

            <p className="text-[11px] text-white/40 mt-4 text-center font-mono">
              RFQ-2847 · 8 suppliers invited · 6 bids · $49.7–53.6 M range
            </p>
          </div>

          {/* 5 sourcing features */}
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5 mt-14">
            {SOURCING_FEATURES.map((f) => (
              <motion.div key={f.n}
                          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }}>
                <div className="text-3xl font-data font-bold text-[#F26B1F]/60 mb-2">{f.n}</div>
                <h3 className="text-sm font-semibold text-white mb-2">{f.title}</h3>
                <p className="text-xs text-white/60 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>

          <p className="text-xl md:text-2xl text-white mt-16 max-w-3xl font-heading leading-snug border-l-2 border-[#F26B1F] pl-6">
            A reverse auction is theatre.
            <span className="italic font-light text-[#F26B1F]"> ProcureX makes it cinema.</span>
          </p>
        </div>
      </section>

      {/* ─────────────────────────── CONTRACT AI ─────────────────────────── */}
      <section id="contracts" className="py-24 px-6">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-xs font-mono uppercase tracking-widest text-[#F26B1F] mb-4">The contract intelligence layer</p>
            <h2 className="text-3xl md:text-5xl font-heading font-bold tracking-tight leading-tight">
              AI that reads contracts like
              <span className="italic font-light text-slate-500"> a 30-year legal counsel.</span>
            </h2>
            <p className="text-base text-slate-600 mt-6 leading-relaxed">
              Built on a proprietary clause corpus — 50,000+ annotated clauses from FIDIC, NEC,
              bespoke Indian capital-project contracts, and arbitration precedents. It reads,
              writes, and reasons about contracts with the fluency of senior legal counsel.
            </p>
            <ul className="mt-8 space-y-3">
              {CONTRACT_AI.map((c, i) => (
                <motion.li key={i}
                           initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }}
                           viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                           className="flex items-start gap-3 text-sm text-slate-700">
                  <CheckCircle className="w-4 h-4 text-[#1E4E8C] flex-shrink-0 mt-0.5" />
                  <span>{c}</span>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Contract analyzer mock */}
          <div className="rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-6 shadow-xl shadow-slate-200">
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Contract Draft · v3</span>
              <span className="flex items-center gap-1.5 text-[10px] text-[#F26B1F]">
                <Brain className="w-3 h-3" /> AI ACTIVE
              </span>
            </div>

            <div className="rounded-lg bg-white border border-slate-200 p-4 mb-4">
              <div className="text-[10px] font-mono text-slate-400 mb-1.5">Clause 14.3 · Liquidated Damages</div>
              <div className="text-sm text-slate-700 leading-relaxed mb-3">
                "Delay damages shall accrue at <span className="bg-amber-100 px-1 rounded">0.5% of Contract Price per week</span>,
                with a cap of <span className="bg-rose-100 text-rose-900 px-1 rounded font-semibold">10%</span>."
              </div>
              <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2.5 py-1.5 flex items-start gap-2">
                <Sparkles className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>LD cap at 10% — below category median (15%). Counsel review recommended.</span>
              </div>
            </div>

            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-3">AI Outputs</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                ['Obligations Tracker', '27 extracted', '#1E4E8C'],
                ['Risk Heatmap', '4 clauses high-risk', '#F26B1F'],
                ['Claim Template', 'Pre-drafted', '#2E70AD'],
                ['Audit Trail', 'Logged', '#10B981'],
              ].map(([t, v, c]) => (
                <div key={t} className="p-2.5 rounded-lg border border-slate-200 bg-white">
                  <div className="text-[10px] text-slate-500">{t}</div>
                  <div className="text-xs font-semibold mt-0.5" style={{ color: c }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────── INTEGRATIONS ─────────────────────────── */}
      <section className="py-24 px-6 bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs font-mono uppercase tracking-widest text-[#F26B1F] mb-4">Built for your stack</p>
          <h2 className="text-3xl md:text-5xl font-heading font-bold tracking-tight leading-tight max-w-3xl">
            Integrates with ERPs, SRMs, banks, and
            <span className="italic font-light text-slate-500"> statutory systems.</span>
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
            {INTEGRATIONS.map((g, i) => (
              <motion.div key={g.title}
                          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                          className="bg-white rounded-xl p-6 border border-slate-200">
                <div className="flex items-center gap-2 mb-4">
                  {[Database, Workflow, Layers, ShieldCheck][i] && (() => {
                    const Icon = [Database, Workflow, Layers, ShieldCheck][i];
                    return <Icon className="w-4 h-4 text-[#1E4E8C]" />;
                  })()}
                  <h3 className="text-sm font-semibold text-slate-900">{g.title}</h3>
                </div>
                <ul className="space-y-1.5 text-xs text-slate-600">
                  {g.items.map((it) => <li key={it} className="leading-relaxed">{it}</li>)}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────────── INDUSTRIES + USE CASES ─────────────────────────── */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs font-mono uppercase tracking-widest text-[#F26B1F] mb-4">Industries & use cases</p>
          <h2 className="text-3xl md:text-5xl font-heading font-bold tracking-tight leading-tight max-w-3xl">
            Built for every procurement team —
            <span className="italic font-light text-slate-500"> from MRO buyers to mega-project owners.</span>
          </h2>

          {/* Industries pills */}
          <div className="flex flex-wrap gap-2 mt-10">
            {INDUSTRIES.map((it) => (
              <span key={it} className="px-3 py-1.5 rounded-full bg-[#1E4E8C]/8 text-[#1E4E8C] text-xs font-medium hover:bg-[#1E4E8C]/15 transition cursor-default">
                {it}
              </span>
            ))}
          </div>

          {/* Use cases grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {USE_CASES.map((u, i) => (
              <motion.div key={u.title}
                          initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                          className="p-6 rounded-xl border border-slate-200 hover:border-[#F26B1F]/40 hover:shadow-md transition-all">
                <h3 className="text-base font-semibold text-slate-900 mb-2">{u.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{u.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────────── IMPACT ─────────────────────────── */}
      <section id="impact" className="py-24 px-6 bg-[#1E4E8C] text-white relative overflow-hidden">
        <div className="absolute -top-20 -left-20 w-[500px] h-[500px] bg-white/5 rounded-full blur-[100px]" />
        <div className="absolute -bottom-20 -right-20 w-[400px] h-[400px] bg-[#F26B1F]/20 rounded-full blur-[120px]" />
        <div className="max-w-6xl mx-auto relative">
          <p className="text-xs font-mono uppercase tracking-widest text-[#F26B1F] mb-4">Measured impact</p>
          <h2 className="text-3xl md:text-5xl font-heading font-bold tracking-tight leading-tight max-w-3xl">
            What enterprises measure after deploying
            <span className="italic font-light text-blue-200"> ProcureX.</span>
          </h2>

          <div className="grid md:grid-cols-3 gap-8 mt-14">
            {IMPACT.map((m, i) => (
              <motion.div key={i}
                          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true }} transition={{ delay: i * 0.12 }}
                          className="border-l-2 border-[#F26B1F] pl-6">
                <p className="font-data text-6xl font-bold text-white">{m.value}</p>
                <p className="text-sm text-blue-100 mt-3 leading-relaxed">{m.label}</p>
                <p className="text-xs text-[#F26B1F] mt-2 font-medium">{m.detail}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────────── FAQ ─────────────────────────── */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-mono uppercase tracking-widest text-[#F26B1F] mb-4">Frequently asked</p>
          <h2 className="text-3xl md:text-5xl font-heading font-bold tracking-tight leading-tight">
            Everything a
            <span className="italic font-light text-slate-500"> CPO or Head of Contracts</span> wants to know.
          </h2>

          <div className="mt-12 divide-y divide-slate-200 border-y border-slate-200">
            {FAQS.map((f, i) => (
              <button key={i}
                      onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                      data-testid={`faq-${i}`}
                      className="w-full text-left py-5 flex items-start gap-4 group">
                <ChevronDown className={`w-4 h-4 mt-1.5 text-slate-400 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180 text-[#F26B1F]' : ''}`} />
                <div className="flex-1">
                  <h3 className="text-base md:text-lg font-medium text-slate-900 group-hover:text-[#1E4E8C] transition">
                    {f.q}
                  </h3>
                  {openFaq === i && (
                    <motion.p initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                              className="text-sm text-slate-600 mt-3 leading-relaxed">
                      {f.a}
                    </motion.p>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────────────────────── CTA ─────────────────────────── */}
      <section className="py-24 px-6 bg-slate-50 border-t border-slate-200">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                      className="rounded-3xl bg-[#0A1E3A] p-10 md:p-16 text-white relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-[400px] h-[400px] bg-[#F26B1F]/20 rounded-full blur-[100px]" />
            <div className="relative">
              <p className="text-xs font-mono uppercase tracking-widest text-[#F26B1F] mb-4">See ProcureX in action</p>
              <h2 className="text-3xl md:text-4xl font-heading font-bold tracking-tight leading-tight max-w-3xl">
                Bring us your hardest sourcing event or active claim —
                <span className="italic font-light text-blue-200"> we'll show you how ProcureX handles it, live.</span>
              </h2>
              <p className="text-base text-blue-100 mt-5 max-w-2xl leading-relaxed">
                Our "Live Sourcing Session" is a 60-minute working demo where we load an anonymised
                version of your actual RFQ (under NDA), configure the sourcing cockpit, run through
                bid analytics, or walk through an active claim with AI-drafted response letters.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Button size="lg" onClick={() => navigate('/trial-signup')}
                        className="bg-[#F26B1F] hover:bg-[#D85A14] text-white h-12 px-8 text-sm font-semibold"
                        data-testid="cta-trial-btn">
                  Start your 1-day trial <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => navigate('/login')}
                        className="h-12 px-8 text-sm border-white/20 text-white hover:bg-white/10 bg-transparent"
                        data-testid="cta-signin-btn">
                  Sign in to my account
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─────────────────────────── FOOTER ─────────────────────────── */}
      <footer className="border-t border-slate-100 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-12 grid md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <img src="/procurex-logo-light.svg" alt="ProcureX" className="h-9 mb-4" />
            <p className="text-sm text-slate-500 max-w-md leading-relaxed">
              The AI-native source-to-settle spine for capital projects. Procurement,
              supply chain, and contracts lifecycle management on one cognitive canvas.
            </p>
          </div>
          <div>
            <h4 className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-3">Platform</h4>
            <ul className="text-sm text-slate-600 space-y-2">
              <li><a href="#architecture" className="hover:text-[#1E4E8C]">Architecture</a></li>
              <li><a href="#sourcing"     className="hover:text-[#1E4E8C]">Sourcing cockpit</a></li>
              <li><a href="#contracts"    className="hover:text-[#1E4E8C]">Contract AI</a></li>
              <li><a href="#impact"       className="hover:text-[#1E4E8C]">Customer impact</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-3">Get in touch</h4>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-center gap-2"><Mail className="w-3.5 h-3.5" />hello@capexara.com</li>
              <li className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" />+91 80 4567 8900</li>
              <li className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5" />Mumbai · Bengaluru</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-100">
          <div className="max-w-6xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-xs text-slate-400">© 2026 ProcureX. Source-to-settle, AI-native.</p>
            <p className="text-xs text-slate-400">SOC-2 Type II · ISO 27001 · ISO 27701</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
